import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react'
import { SnackbarHost } from '../../components/ui/snackbar'
import { applyTheme, readTheme, saveTheme, type Theme } from './theme'

interface ThemeState {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeState | null>(null)

/** Holds the theme choice; main.tsx applies the kept one before the first render, so nothing flashes. */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setState] = useState(readTheme)
  const setTheme = useCallback((next: Theme) => {
    saveTheme(next)
    applyTheme(next)
    setState(next)
  }, [])
  const value = useMemo(() => ({ theme, setTheme }), [theme, setTheme])
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme(): ThemeState {
  const state = useContext(ThemeContext)
  if (!state) throw new Error('useTheme outside ThemeProvider')
  return state
}

/** The snackbar host (sonner on the tokens) in the same theme as the page. */
export function ThemedToaster() {
  return <SnackbarHost theme={useTheme().theme} />
}
